"use client";

import { useContext, useEffect } from "react";
import { AppContext } from "@/context/AppContext";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { WorkCase } from "@/data/types";
import { useData, useLocalePath } from "@/i18n/LangContext";
import { ExternalLink, Store, Download } from "lucide-react";

interface WorkGridProps {
  projects: WorkCase[];
  previewNotAvailable: string;
  moreDetailsText: string;
}

export function WorkGrid({ projects, previewNotAvailable, moreDetailsText }: WorkGridProps) {
  const { setActivePage } = useContext(AppContext);
  const { ui } = useData();
  const lp = useLocalePath();

  // "YYYY-MM" → sprachrichtiger Monatsname + Jahr (z.B. "März 2025" / "March 2025")
  const formatMonthYear = (isoMonth: string) => {
    const match = /^(\d{4})-(\d{2})$/.exec(isoMonth);
    if (!match) return isoMonth;
    return new Date(Number(match[1]), Number(match[2]) - 1, 1)
      .toLocaleDateString(ui.dateLocale, { month: "long", year: "numeric" });
  };

  useEffect(() => {
    setActivePage("work");
  }, [setActivePage]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 50, damping: 15 }
    },
    hover: {
      y: -5,
      scale: 1.02,
      transition: { type: "spring", stiffness: 150, damping: 20 }
    }
  };

  return (
    <motion.div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {projects.map((project, index) => (
        <motion.div
          key={project.id}
          data-cy="work-case"
          className="rounded-xl overflow-hidden border border-[#ff88ff]/20 bg-[rgba(30,12,50,0.3)] backdrop-blur-sm relative"
          variants={cardVariants}
          whileHover="hover"
        >
          {project.detailContent && (
            <Link
              href={lp(`/work/${project.slug}`)}
              className="absolute inset-0 z-10 cursor-pointer"
              aria-label={ui.workDetail.learnMoreAbout.replace('{title}', project.title)}
            >
              <span className="sr-only">{ui.workDetail.learnMoreAbout.replace('{title}', project.title)}</span>
            </Link>
          )}
          
          <div className="relative h-48 overflow-hidden">
            {project.image ? (
              <div className="relative h-full w-full bg-[#1E0B32] flex items-center justify-center">
                <Image
                  src={project.image}
                  alt={project.title}
                  fill
                  priority={index < 3}
                  style={{ objectFit: "cover", objectPosition: "0 -40px" }}
                  className="transition-all duration-500 hover:scale-105"
                  sizes="(max-width: 768px) 100vw, (max-width: 1152px) 50vw, 560px"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1E0B32]/80" />
              </div>
            ) : (
              <div className="h-full w-full bg-[#1E0B32] flex items-center justify-center">
                <span className="text-[#ff88ff]">{previewNotAvailable}</span>
              </div>
            )}

            <motion.div
              className="absolute top-4 right-4 bg-[#1E0B32] p-2 rounded-lg z-20"
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
            >
              {project.link && (
                <motion.a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#ff88ff]"
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                </motion.a>
              )}
            </motion.div>
          </div>

          <div className="p-6">
            <h3 className="text-xl font-bold mb-2 text-[#ff88ff]">{project.title}</h3>
            {project.date && (
              <p className="text-xs text-[#ff88ff]/60 uppercase tracking-wider mb-3">{formatMonthYear(project.date)}</p>
            )}
            <p className="text-content mb-4">{project.description}</p>
            <div data-cy="work-case-tags" className="flex flex-wrap gap-2 mb-4">
              {project.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-[#1E0B32] text-[#ff88ff] rounded-full text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
            {(project.appStoreUrl || project.websiteUrl || project.links?.length) && (
              <div data-cy="work-case-app-links" className="flex flex-wrap gap-3 mb-4 relative z-20">
                {project.links?.[0] && (
                  <a
                    href={project.links[0].url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff88ff] text-[#1E0B32]! rounded-full text-sm font-semibold hover:bg-[#ff88ff]/85 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download size={16} strokeWidth={2} />
                    {project.links[0].label}
                  </a>
                )}
                {project.appStoreUrl && (
                  <a
                    href={project.appStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff88ff] text-[#1E0B32]! rounded-full text-sm font-semibold hover:bg-[#ff88ff]/85 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Store size={16} strokeWidth={2} />
                    {ui.workDetail.appStoreDownload}
                  </a>
                )}
                {project.websiteUrl && (
                  <a
                    href={project.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#ff88ff]/40 text-[#ff88ff] rounded-full text-sm font-semibold hover:border-[#ff88ff] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink size={16} strokeWidth={2} />
                    Website
                  </a>
                )}
              </div>
            )}
            {/* Nur für Projekte mit detaillierten Inhalten einen Detail-Link anzeigen */}
            {project.detailContent && (
              <Link
                href={lp(`/work/${project.slug}`)}
                className="inline-flex items-center text-[#ff88ff] hover:text-[#ff88ff]/80 transition-colors relative z-20"
                onClick={(e) => e.stopPropagation()}
              >
                {moreDetailsText}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="ml-2"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </Link>
            )}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}